import React, { useRef, useState } from "react";

export default function BulkPrintDropzone({
  uploaded,
  disabled,
  processing,
  onFiles,
  printCount,
  total,
}) {
  const inputRef = useRef(null);
  const [isDragging, setIsDragging] = useState(false);

  const handleDrop = (e) => {
    e.preventDefault();
    setIsDragging(false);
    if (disabled || processing) return;
    const files = e.dataTransfer?.files;
    if (files?.length) onFiles?.(files);
  };

  if (processing) {
    return (
      <div className="mt-4 rounded-[14px] px-[18px] py-3 flex items-center gap-3 bg-lightgray border border-mediumgray">
        <div className="editor-upload-spinner" />
        <span className="text-[13px] font-bold">
          Uploading print masters…
        </span>
      </div>
    );
  }

  if (uploaded) {
    return (
      <div className="mt-4 rounded-[14px] px-[18px] py-3 flex items-center gap-3 bg-lightgray border border-mediumgray">
        <div className="w-[34px] h-[34px] rounded-full bg-black text-white flex items-center justify-center flex-none text-sm">
          ✓
        </div>
        <span className="text-[13px] font-bold">
          Photos uploaded · {printCount}/{total} print masters
        </span>
        <button
          type="button"
          disabled={disabled}
          onClick={() => inputRef.current?.click()}
          className="ml-auto border border-mediumgray bg-white rounded-lg px-3.5 py-1.5 text-[12px] font-semibold hover:bg-lightgray transition-colors disabled:opacity-50"
        >
          Re-upload batch
        </button>
        <input
          ref={inputRef}
          type="file"
          accept="image/*"
          multiple
          className="hidden"
          onChange={(e) => {
            if (e.target.files?.length) onFiles?.(e.target.files);
            e.target.value = "";
          }}
        />
      </div>
    );
  }

  return (
    <div
      role="button"
      tabIndex={0}
      aria-label="Upload print masters"
      onDragOver={(e) => {
        e.preventDefault();
        if (!disabled) setIsDragging(true);
      }}
      onDragLeave={() => setIsDragging(false)}
      onDrop={handleDrop}
      onClick={() => {
        if (!disabled) inputRef.current?.click();
      }}
      onKeyDown={(e) => {
        if (e.key === "Enter" || e.key === " ") {
          e.preventDefault();
          if (!disabled) inputRef.current?.click();
        }
      }}
      className={[
        "mt-4 rounded-[14px] border-[1.5px] border-dashed border-mediumgray px-6 py-10 text-center cursor-pointer bg-lightgray",
        "hover:border-black transition-all duration-200",
        isDragging ? "editor-dropzone-drag" : "",
        disabled ? "opacity-60 pointer-events-none" : "",
      ].join(" ")}
    >
      <div className="w-[52px] h-[52px] rounded-full bg-black text-white flex items-center justify-center mx-auto mb-3.5 text-xl">
        ↑
      </div>
      <div className="text-[16px] font-bold tracking-tight mb-1.5">
        Drop your edited photos here
      </div>
      <div className="text-[13px] text-darkgray max-w-[440px] mx-auto mb-4 leading-relaxed">
        Add all print-resolution masters for this job at once — web versions
        generate automatically in the background.
      </div>
      <button
        type="button"
        className="bg-black text-white font-bold text-[13px] px-6 py-2.5 rounded-lg hover:opacity-85 transition-opacity"
      >
        Choose files to upload
      </button>
      <div className="text-[11.5px] text-darkgray mt-2.5">
        or drag images onto this area · print masters only (≥5MB)
      </div>
      <input
        ref={inputRef}
        type="file"
        accept="image/*"
        multiple
        className="hidden"
        onChange={(e) => {
          if (e.target.files?.length) onFiles?.(e.target.files);
          e.target.value = "";
        }}
      />
    </div>
  );
}
